import React from 'react';
import { Link } from 'react-router-dom';
import { HeartFill } from 'react-bootstrap-icons';
import Button from './Button';
import SmartImage from './SmartImage';
import Rating from './Rating';
import { assetUrl } from '../../config/api';
import { discountPercent, finalPrice, formatPrice, listPrice } from '../../config/format';

/**
 * The single product card used by every listing surface.
 *
 * It previously existed as six near-identical copies (Home, HotProducts,
 * NewArrival, AlsoLike, Search, Productshow's "may you also like"), each with
 * its own subtly different markup and its own bug fixes.
 */
const ProductCard = ({
  product,
  to,
  isWishlisted = false,
  onToggleWishlist,
  wishlistPending = false,
  onAddToCart,
  cartPending = false,
}) => {
  if (!product) return null;

  const {
    product_code: productCode,
    product_name: productName,
    product_image_1: image,
    single_product: singleProduct,
    top_review: topReview,
    variant,
  } = product;

  const href = to || `/productshow/${productCode}`;
  const discount = discountPercent(product);
  const hasDiscount = discount > 0;
  const quantity = Number(variant?.quantity);
  const isSingle = Number(singleProduct) > 0;
  const outOfStock = isSingle && quantity === 0;
  const lowStock = isSingle && quantity > 0 && quantity < 15;

  return (
    <article className="ij-card">
      <div className="ij-card__media">
        {hasDiscount && (
          <span className="ij-badge ij-badge--sale ij-card__badge">
            {discount}% off
          </span>
        )}

        {onToggleWishlist && (
          <button
            type="button"
            className="ij-card__wishlist"
            aria-pressed={isWishlisted}
            aria-label={
              isWishlisted
                ? `Remove ${productName} from wishlist`
                : `Add ${productName} to wishlist`
            }
            disabled={wishlistPending}
            onClick={() => onToggleWishlist(productCode)}
          >
            <HeartFill size={17} aria-hidden="true" />
          </button>
        )}

        <Link to={href} tabIndex={-1} aria-hidden="true">
          <SmartImage
            className="ij-card__img"
            src={assetUrl(image)}
            alt=""
          />
        </Link>
      </div>

      <div className="ij-card__body">
        <Link to={href} className="ij-card__link">
          <h3 className="ij-card__title">{productName}</h3>
          {topReview?.rating > 0 && <Rating value={topReview.rating} />}
          <p className="ij-card__price">
            {formatPrice(finalPrice(product))}
            {hasDiscount && (
              <span className="ij-card__price-was">
                {formatPrice(listPrice(product))}
              </span>
            )}
          </p>
        </Link>

        {/* Only rendered when there is something to say — the grid keeps
            every card in a row the same height, so no spacer is needed. */}
        {(outOfStock || lowStock) && (
          <p
            className={`ij-card__stock ${
              outOfStock ? 'ij-card__stock--out' : 'ij-card__stock--low'
            }`}
          >
            {outOfStock ? 'Out of stock' : `Only ${quantity} left`}
          </p>
        )}

        <div className="ij-card__actions">
          {Number(singleProduct) === 1 ? (
            <Button
              variant="primary"
              block
              loading={cartPending}
              loadingText="Adding…"
              disabled={outOfStock}
              onClick={() => onAddToCart?.(variant?.id, productCode)}
            >
              {outOfStock ? 'Out of stock' : 'Add to cart'}
            </Button>
          ) : (
            <Link to={href} className="ij-btn ij-btn--outline ij-btn--block">
              Choose options
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
